import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Check, X, Info } from "lucide-react";

const STAGE_TONE = {
  awaiting_attorney_approval: "bg-amber-50 text-amber-600", awaiting_client_authorization: "bg-amber-50 text-amber-600",
  offered_to_provider: "bg-cobalt-light text-cobalt", awaiting_client_scheduling: "bg-cobalt-light text-cobalt",
  forms_pending: "bg-amber-50 text-amber-600", appointment_confirmed: "bg-emerald-50 text-emerald-600",
  visit_completed: "bg-emerald-50 text-emerald-600", expired: "bg-red-50 text-red-600",
  declined: "bg-red-50 text-red-600", rescheduling_required: "bg-red-50 text-red-600",
};

export const TelehealthPipeline = ({ reloadParent }) => {
  const [data, setData] = useState(null);
  const [prepared, setPrepared] = useState([]);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const load = useCallback(() => {
    api.get("/attorney/telehealth/pipeline").then(({ data: d }) => setData(d)).catch(() => setData({ rows: [], counts: {} }));
    api.get("/attorney/telehealth/prepared").then(({ data: d }) => setPrepared(d)).catch(() => {});
    api.get("/attorney/telehealth/automation-log").then(({ data: d }) => setLog(d)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, decision) => {
    try {
      await api.post(`/attorney/telehealth/prepared/${rid}/decide`, { decision });
      toast.success(decision === "approve" ? "Referral approved — transmission proceeding" : "Referral rejected");
      load(); if (reloadParent) reloadParent();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  return (
    <div data-testid="ath-pipeline">
      {prepared.length > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="ath-prepared">
          <div className="text-sm font-semibold text-amber-700 mb-2">Prepared referrals awaiting your approval</div>
          {prepared.map((p) => (
            <div key={p.request_id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm">
              <div>
                <span className="font-mono text-xs text-slate-500">{p.case_number}</span>
                <span className="ml-2 text-navy font-medium">→ {p.provider_name}</span>
                {p.match_info?.reasons && <div className="text-[10px] text-slate-500 mt-0.5">{p.match_info.reasons.join(" · ")}</div>}
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => decide(p.request_id, "approve")} data-testid={`ath-approve-${p.request_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold"><Check className="w-3 h-3" /> Approve</button>
                <button onClick={() => decide(p.request_id, "reject")} data-testid={`ath-reject-${p.request_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs"><X className="w-3 h-3" /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {Object.entries(data.counts).map(([stage, n]) => (
          <span key={stage} className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${STAGE_TONE[stage] || "bg-slate-100 text-slate-500"}`} data-testid={`ath-count-${stage}`}>
            {stage.replace(/_/g, " ")}: {n}
          </span>
        ))}
      </div>

      {data.rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="ath-pipeline-empty">No telehealth clients in the pipeline yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
              <th className="px-2 py-2">Client</th><th className="px-2 py-2">Case</th><th className="px-2 py-2">Provider</th>
              <th className="px-2 py-2">Stage</th><th className="px-2 py-2">Appointment</th><th className="px-2 py-2">Next action</th><th className="px-2 py-2">Responsible</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.request_id} className={`border-b border-slate-50 last:border-0 ${r.alert ? "bg-red-50/40" : ""}`} data-testid={`ath-row-${r.request_id}`}>
                  <td className="px-2 py-2 text-navy font-medium">{r.client_name || "—"}</td>
                  <td className="px-2 py-2 font-mono text-xs text-slate-400">{r.case_number}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{r.provider_name}</td>
                  <td className="px-2 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${STAGE_TONE[r.stage] || "bg-slate-100 text-slate-500"}`}>{r.stage.replace(/_/g, " ")}</span>
                    {r.forms_open > 0 && <div className="text-[10px] text-amber-600 mt-0.5">{r.forms_open} form(s) open</div>}</td>
                  <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{r.appointment_start ? new Date(r.appointment_start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</td>
                  <td className="px-2 py-2 text-xs text-slate-500">{r.next_action}</td>
                  <td className="px-2 py-2 text-xs text-slate-400">{r.responsible}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => setShowLog(!showLog)} data-testid="ath-toggle-log" className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
        <Info className="w-3.5 h-3.5" /> Why did automation fire (or not)? {showLog ? "▲" : "▼"}
      </button>
      {showLog && (
        <div className="mt-2 divide-y divide-slate-50 rounded-2xl border border-slate-100 p-3" data-testid="ath-automation-log">
          {log.length === 0 ? <div className="py-4 text-center text-xs text-slate-400">No automation evaluations yet.</div> :
            log.slice(0, 20).map((l) => (
              <div key={l.log_id} className="py-1.5 text-[11px] flex flex-wrap justify-between gap-2">
                <span><span className="font-mono text-slate-400">{l.case_number}</span>
                  <span className={`ml-2 font-semibold ${l.fired ? "text-emerald-600" : "text-slate-500"}`}>{l.fired ? `FIRED (${l.action})` : "did not fire"}</span>
                  <span className="ml-2 text-slate-500">{l.reason}</span>
                  <span className="ml-1 text-slate-300">· trigger {l.trigger} · mode {l.mode || "—"}</span></span>
                <span className="text-slate-400 whitespace-nowrap">{new Date(l.at).toLocaleString()}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default TelehealthPipeline;
